export interface HealthZone {
  id: number;
  rank: 'Attention' | 'Maintain' | 'Improve';
  image_url: string;
  score: number;
  title: string;
  rank_color: string;
}

export type HealthZonesResponse = HealthZone[];

export interface BiologicalAge {
  actual_age: number;
  biological_age: number;
  max_age: number;
  message: string;
}

export interface Inflammaging {
  message: string;
  ranking: string;
}

export interface MyLongevity {
  biological_age: BiologicalAge;
  inflammaging: Inflammaging;
}

export type MyLongevityResponse = MyLongevity;

export interface MicrobiomeProfile {
  id: string;
  title: string;
}

export type MicrobiomeProfileResponse = MicrobiomeProfile[];

export interface ActiveMicrobe {
  name: string;
  super_kingdom: string;
}

export interface MyActiveMicrobes {
  about: string;
  active_microbes: ActiveMicrobe[];
  title: string;
}

export type MyActiveMicrobesResponse = MyActiveMicrobes;

export interface RichnessTrend {
  date: string;
  message: string;
  percentage: number;
  sequence_no: number;
}

export interface Dysbiosis {
  about: string;
  id: number;
  message: string;
  rank: IHealthStatus;
  rank_color: string;
  title: string;
  trend: InflammagingTrend[];
}

export interface Richness {
  about: string;
  percentage: number;
  rank: IHealthStatus;
  rank_color: string;
  title: string;
  trend: RichnessTrend[];
}

export interface MicrobiomeDetail {
  about: string;
  dysbiosis: Dysbiosis;
  richness: Richness;
  title: string;
}

export type MicrobiomeDetailResponse = MicrobiomeDetail;

export interface HealthConnection {
  connection_type: string;
  connection_type_color: string;
  name: string;
  no_of_connections: number;
}

export type HealthConnectionsResponse = HealthConnection[];

export interface BiologicalAgeData {
  about: string;
  actual_age: number;
  biological_age: number;
  max_age: number;
  message: string;
  score_id: string;
}

export type BiologicalAgeResponse = BiologicalAgeData;

export interface HealthConnectionsListData {
  connections: HealthConnection[];
  description: string;
  title: string;
}

export type HealthConnectionsListResponse = HealthConnectionsListData;

export interface CommonlyLinkedSymptoms {
  commonly_linked_symptoms: string[];
}

export type CommonlyLinkedSymptomsResponse = CommonlyLinkedSymptoms;

export interface DoctorNotes {
  description: string;
  image_url?: string;
  title?: string;
}

export type IDoctorsNoteResponse = DoctorNotes[];

export interface L4HealthConnection {
  connection_type: string;
  connection_type_color: string;
  id: number;
  name: string;
  no_of_connections: number;
}

export type L4HealthConnectionListResponse = L4HealthConnection[];

export interface ScoreRange {
  high_range: number;
  low_range: number;
  percentile: number;
  rank: IHealthStatus;
  rank_color: string;
}

export interface ScoreReferenceData {
  score_range: ScoreRange[];
  score_samples: string[];
}

export type IScoreReferenceResponse = ScoreReferenceData;

export interface InflammagingTrend {
  date: string;
  message: string;
  rank: IHealthStatus;
  rank_color: string;
  sequence_no: number;
}

export interface InflammagingData {
  about: string;
  message: string;
  rank: IHealthStatus;
  rank_color: string;
  title: string;
  trend: InflammagingTrend[];
}

export type InflammagingDetailResponse = InflammagingData;

export interface Connection {
  id: number;
  name: string;
  rank: IHealthStatus;
  rank_color: string;
}

export interface ConnectionDetail {
  connections: Connection[];
  description: string;
  no_of_connections: number;
  tag: string;
  tag_color: string;
  title: string;
}

export type ConnectionDetailResponse = ConnectionDetail;

export interface ConnectionDetailL4Detail {
  links: string[];
  name: string;
  rank: IHealthStatus;
  rank_color: string;
  reason: string;
  result: string;
  score: number;
}

export type ConnectionDetailL4DetailResponse = ConnectionDetailL4Detail;

export interface BoosterAndBlockerSummary {
  count: number;
  image_url: string[];
}

export type BoosterAndBlockerSummaryResponse = BoosterAndBlockerSummary;

export type IContributingScoreResponse = ContributingScore[];

export interface ContributingScore {
  id: number;
  rank: IHealthStatus;
  rank_color: string;
  scores: number;
  title: string;
  score_type: IScoreLevel;
}

export const HealthStatus = {
  Attention: 'Attention',
  Improve: 'Improve',
  Maintain: 'Maintain',
} as const;

export type IHealthStatus = (typeof HealthStatus)[keyof typeof HealthStatus];

export const ScoreLevel = {
  L1: 'L1',
  L2: 'L2',
  L3: 'L3',
  L4: 'L4',
} as const;

export type IScoreLevel = (typeof ScoreLevel)[keyof typeof ScoreLevel];

export type IScoreDetailResponse = ScoreDetail;

export interface ScoreDetail {
  about: string;
  contributing_scores?: number;
  image_url: string;
  rank: IHealthStatus;
  rank_color: string;
  score: number;
  title: string;
  trend?: Trend[];
  related_health_zones?: RelatedHealthZone[];
}

export interface Trend {
  color: string;
  date: string;
  score: number;
  sequence_no: number;
}
export interface RelatedHealthZone {
  id: number;
  image_url: string;
  title: string;
}

export interface HealthZoneDetail {
  about: string;
  contributing_scores: number;
  image_url: string;
  rank: IHealthStatus;
  rank_color: string;
  score: number;
  title: string;
  trend: Trend[];
}

export type HealthZoneDetailResponse = HealthZoneDetail;

export interface ImproveScoreSummary {
  id: number;
  image_url: string[];
  number_of_foods_and_ingredients: number;
}

export interface ImproveScoreItem {
  item_id: number;
  item_tag: string;
  item_title: string;
  item_type: 'Foods' | 'Ingredients';
  item_url: string;
}

export interface ImproveScoreDetailResponse {
  items: ImproveScoreItem[];
  score_name: string | null;
  goal: string | null;
}

export interface ImproveScoreParams {
  score_id?: number;
  report_type?: string;
  condition_name?: string;
}
